/**
 * Add Indicator Dialog Component
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Loader2, Settings2 } from 'lucide-react';
import { type DashboardIndicator } from '../../../lib/api/reporting';
import { REPORT_VIEW_TYPES } from '../constants';
import { NewIndicatorState } from '../useReports';

interface AddIndicatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  newIndicator: NewIndicatorState;
  onIndicatorChange: (indicator: NewIndicatorState) => void;
  saving: boolean;
  /**
   * Submit handler. Should return the created indicator (or null on
   * failure) so callers can chain into the Report Builder.
   */
  onSubmit: () => Promise<DashboardIndicator | null> | void;
  /**
   * Optional callback fired after successful creation when the user
   * elected to customise the indicator visually.
   */
  onCustomiseAfterCreate?: (indicator: DashboardIndicator) => void;
}

export function AddIndicatorDialog({
  open,
  onOpenChange,
  newIndicator,
  onIndicatorChange,
  saving,
  onSubmit,
  onCustomiseAfterCreate,
}: AddIndicatorDialogProps) {
  const handleAdd = async () => {
    await onSubmit();
  };
  const handleAddAndCustomise = async () => {
    const result = await onSubmit();
    if (result && onCustomiseAfterCreate) {
      onCustomiseAfterCreate(result);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Chart</DialogTitle>
          <DialogDescription>
            Add a new chart or visualization to the report.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Chart Name *</Label>
            <Input
              placeholder="Enter chart name"
              value={newIndicator.name}
              onChange={(e) => onIndicatorChange({ ...newIndicator, name: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label>Chart Type *</Label>
            <div className="grid grid-cols-3 gap-3">
              {REPORT_VIEW_TYPES.map((type) => (
                <div
                  key={type.value}
                  className={`p-3 border rounded-lg cursor-pointer hover:border-primary transition-colors ${
                    newIndicator.chart_type === type.value
                      ? 'border-primary bg-primary/5'
                      : 'border-border'
                  }`}
                  onClick={() => onIndicatorChange({ ...newIndicator, chart_type: type.value as DashboardIndicator['chart_type'] })}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {type.icon}
                    <span className="font-medium text-sm">{type.label}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{type.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              placeholder="Enter description (optional)"
              value={newIndicator.hint}
              onChange={(e) => onIndicatorChange({ ...newIndicator, hint: e.target.value })}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd} disabled={!newIndicator.name.trim() || saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Chart
          </Button>
          {onCustomiseAfterCreate && (
            <Button
              variant="default"
              onClick={handleAddAndCustomise}
              disabled={!newIndicator.name.trim() || saving}
            >
              {saving
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <Settings2 className="h-4 w-4 mr-2" />}
              Add & Customise
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
